<!-- eslint-disable tailwindcss/no-custom-classname -->
<template>
  <Transition enter-from-class="opacity-0" leave-to-class="opacity-0">
    <RevBackdrop
      v-if="isVisible"
      class="bg-overlay-low z-20 transition-opacity duration-200 md:hidden"
    />
  </Transition>
  <Transition
    enter-active-class="transition-all duration-200 ease-out"
    enter-from-class="translate-y-full opacity-0 md:-translate-y-56"
    enter-to-class="translate-y-0 opacity-100 md:translate-y-0"
    leave-active-class="transition-all duration-200 ease-in"
    leave-from-class="translate-y-0 opacity-100 md:translate-y-0"
    leave-to-class="translate-y-full opacity-0 md:-translate-y-56"
  >
    <div
      v-if="isVisible"
      ref="atcPopover"
      :aria-describedby="hasError ? 'atc-popover-error-description' : undefined"
      aria-labelledby="atc-popover-title"
      aria-modal="true"
      class="bg-surface-default-low shadow-long rounded-t-lg fixed bottom-0 left-0 z-20 flex max-h-[90vh] w-full flex-col overflow-hidden p-16 transition-all md:rounded-lg md:bottom-auto md:left-auto md:right-24 md:top-[86px] md:max-h-[calc(100vh-96px)] md:w-[320px] lg:right-[calc((100vw-1120px)/2)]"
      :role="hasError ? 'alertdialog' : 'dialog'"
      tabindex="-1"
    >
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-x-8">
          <IconCheckInCircleFilled
            v-if="!hasError"
            class="text-static-success-hi shrink-0"
            size="20"
          />

          <p id="atc-popover-title" class="heading-3">
            {{
              hasError
                ? i18n(translations.errorTitle)
                : i18n(translations.successTitle)
            }}
          </p>
        </div>
        <RevButtonIcon
          :aria-label="i18n(translations.closeLabel)"
          class="shrink-0"
          :icon="IconCrossSmall"
          size="small"
          variant="ghost"
          @click="hide"
        />
      </div>

      <div
        v-if="hasError || product"
        class="divide-static-default-low shrink divide-y overflow-hidden overflow-y-auto"
        :class="hasError && errorBlocking ? 'mt-24' : 'mt-8'"
      >
        <div>
          <RevInfoBlock
            v-if="hasError"
            id="atc-popover-error-description"
            class="mb-16"
            :icon="IconWarning"
            :title="i18n(translations.errorSubtitle)"
            variant="danger"
          >
            <p class="body-2">
              {{ i18n(translations.errorMessage) }}
            </p>
          </RevInfoBlock>
          <ProductCardContent
            v-if="product && !(hasError && errorBlocking)"
            class="-mx-16 mb-8"
            :options="{
              format: 'compact',
              withMarketingTags: false,
              withDeal: false,
              withWarranty: false,
              forceHideAddToCart: true,
            }"
            :product
            @link-click="handleProductClick(product.rawProduct, 1)"
          />
        </div>
        <div
          class="overflow-hidden"
          :class="
            showRecommendations
              ? 'max-h-[600px] opacity-100 transition-[max-height,opacity] duration-[800ms] ease-out'
              : 'max-h-0 opacity-0'
          "
        >
          <div class="pb-8 pt-24">
            <p class="heading-2">{{ i18n(translations.goesWellWith) }}</p>
            <div>
              <div
                v-for="(recommendation, index) in recommendations"
                :key="recommendation.listingId"
                class="relative"
              >
                <ProductCardSkeleton
                  class="skeleton-fade-out absolute inset-0 pt-16"
                  :style="{ animationDelay: `${0.5 + index * 0.5}s` }"
                />
                <ProductCardContent
                  class="card-fade-in -mx-16"
                  list="add_to_cart_popup"
                  :options="{
                    format: 'compact',
                    withWarranty: false,
                    addToCartSuccessMode: 'silent',
                    addToCartErrorMode: 'inline',
                  }"
                  :product="recommendation"
                  :style="{ animationDelay: `${0.5 + index * 0.5}s` }"
                  @link-click="
                    handleProductClick(recommendation.rawProduct, index + 2)
                  "
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <RevButton
        class="w-full"
        :to="{ name: 'checkout_cart', params: { locale } }"
        :tracking="{
          zone: 'add_to_cart_popup',
          name: 'go_to_cart',
        }"
        variant="primary"
      >
        {{ i18n(translations.goToCartButton) }}
      </RevButton>
    </div>
  </Transition>
</template>

<script lang="ts" setup>
import { useRoute } from '#imports'
import { computed, useTemplateRef, watch } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useI18nLocale } from '@backmarket/nuxt-module-i18n/useI18nLocale'
import { RevBackdrop } from '@ds/components/Backdrop'
import { RevButton } from '@ds/components/Button'
import { RevButtonIcon } from '@ds/components/ButtonIcon'
import { RevInfoBlock } from '@ds/components/InfoBlock'
import { IconCheckInCircleFilled } from '@ds/icons/IconCheckInCircleFilled'
import { IconCrossSmall } from '@ds/icons/IconCrossSmall'
import { IconWarning } from '@ds/icons/IconWarning'
import { onKeyStroke, useFocusWithin } from '@vueuse/core'

import type { Product } from '@backmarket/nuxt-layer-recommendation/models/product'
import { useClickTracking } from '@backmarket/nuxt-layer-recommendation/useProductTracking'

import { useAddToCartPopover } from '../../composables/useAddToCartPopover'
import ProductCardContent from '../ProductCard/ProductCardContent.vue'

import translations from './AddToCartPopover.translations'
import ProductCardSkeleton from './components/ProductCardSkeleton.vue'

const route = useRoute()
const { trackProductClick } = useClickTracking()

const { isVisible, product, hasError, errorBlocking, hide, recommendations } =
  useAddToCartPopover()
const locale = useI18nLocale()
const i18n = useI18n()
const atcPopoverRef = useTemplateRef('atcPopover')

const showRecommendations = computed(
  () =>
    recommendations.value.length > 0 &&
    !(hasError.value && errorBlocking.value),
)

const { focused: isFocusedWithin } = useFocusWithin(atcPopoverRef)

onKeyStroke('Escape', () => {
  if (isVisible.value) {
    hide()
  }
})

watch(isVisible, (visible) => {
  if (visible) {
    setTimeout(() => {
      atcPopoverRef.value?.focus()
    }, 100)
  }
})

watch(isFocusedWithin, (focused) => {
  if (!focused && isVisible.value) {
    setTimeout(() => {
      if (!isFocusedWithin.value && isVisible.value) {
        hide()
      }
    }, 150)
  }
})

watch(
  () => route.name,
  () => {
    hide()
  },
)

function handleProductClick(clickedProduct: Product, index: number) {
  trackProductClick(clickedProduct, {
    provider: 'algolia',
    list: 'add_to_cart_popup',
    position: index,
  })
}
</script>

<style scoped>
@keyframes skeleton-fade-out {
  0% {
    opacity: 1;
  }
  100% {
    opacity: 0;
    pointer-events: none;
  }
}

@keyframes card-fade-in {
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
}

.skeleton-fade-out {
  animation: skeleton-fade-out 0.3s ease-out forwards;
}

.card-fade-in {
  animation: card-fade-in 0.3s ease-out forwards;
  opacity: 0;
}
</style>
